import { Location, NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { DatePickerComponent, DateRangeMode } from 'components/date-picker/date-picker.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { AuthorizationService, USER_SECURITY_INFO_KEY } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { Breadcrumb } from 'types/breadcrumb.interface';
import { AuthenticationType, CLIENT_STATUS_UNFINISHED, CLIENT_TYPE_INDIVIDUAL, ClientCard } from 'types/client.interface';
import { CountryDto, CountryDtoPagedResult } from 'types/country.interface';
import {
  MAX_LENGTH_ADDRESS,
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_PHONE,
  MIN_LENGTH_ADDRESS,
  MIN_LENGTH_EMAIL,
  MIN_LENGTH_PHONE,
  atLeastOneContactInfoRequired,
  onlyOneAuthFieldAllowed,
  emailValidator,
  getControlsWithErrors,
  phoneValidator
} from 'util/form.utility';

const PLANNED_AUTH_TYPE_TO_API: Record<string, AuthenticationType> = {
  latviaLv: 'NationalId',
  email: 'Email',
  unknown: 'Unknown'
};

@Component({
  selector: 'app-add-client-page',
  standalone: true,
  imports: [BreadcrumbsComponent, DatePickerComponent, TranslateModule, NgClass, ReactiveFormsModule, TooltipComponent, SpinnerComponent],
  templateUrl: './add-client-page.component.html',
  styleUrl: './add-client-page.component.scss'
})
export class AddClientPageComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [
    {
      title: 'components.breadcrumbs.clientList',
      url: '/klienti'
    },
    {
      title: 'components.breadcrumbs.addClient',
      url: '/klienti/pievienot'
    }
  ];
  isLoading: boolean = true;
  submitAttempted: boolean = false;
  countries: CountryDto[] = [];
  public readonly dateRangeMode = DateRangeMode;

  form: FormGroup;
  get name(): AbstractControl<any, any> | null {
    return this.form.get('name');
  }
  get surname(): AbstractControl<any, any> | null {
    return this.form.get('surname');
  }
  get nationalDigitalId(): AbstractControl<any, any> | null {
    return this.form.get('nationalDigitalId');
  }
  get officialDocumentId(): AbstractControl<any, any> | null {
    return this.form.get('officialDocumentId');
  }
  get authEmail(): AbstractControl<any, any> | null {
    return this.form.get('authEmail');
  }
  get email(): AbstractControl<any, any> | null {
    return this.form.get('email');
  }
  get phone(): AbstractControl<any, any> | null {
    return this.form.get('phone');
  }
  get address(): AbstractControl<any, any> | null {
    return this.form.get('address');
  }
  get countryId(): AbstractControl<any, any> | null {
    return this.form.get('countryId');
  }
  get isIdVerifiedInPerson(): AbstractControl<any, any> | null {
    return this.form.get('isIdVerifiedInPerson');
  }
  get verifierFullName(): AbstractControl<any, any> | null {
    return this.form.get('verifierFullName');
  }
  get nationalDigitalIdId(): AbstractControl<any, any> | null {
    return this.form.get('nationalDigitalIdId');
  }
  get birthday(): AbstractControl<any, any> | null {
    return this.form.get('birthday');
  }

  get plannedAuthType() {
    return this.form.get('plannedAuthType');
  }

  constructor(
    private apiService: ApiService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private router: Router,
    private location: Location,
    private authService: AuthorizationService
  ) {
    this.form = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.apiService.getCountries().subscribe((countries: CountryDtoPagedResult) => {
      this.countries =
        countries.list?.sort((a, b) => {
          if (a.code === 'LV') return -1;
          if (b.code === 'LV') return 1;
          return 0;
        }) ?? [];

      const latviaId = this.countries.find((c) => c.code === 'LV')?.id;
      if (latviaId && this.form.get('countryId')?.value === -1) {
        this.form.get('countryId')?.setValue(latviaId);
      }

      this.isLoading = false;
    });

    this.fillForm();
    this.form.get('isIdVerifiedInPerson')?.valueChanges.subscribe((val) => {
      const officialDocumentIdControl = this.form.get('officialDocumentId');

      if (val === true) {
        officialDocumentIdControl?.addValidators(Validators.required);
      } else {
        officialDocumentIdControl?.removeValidators(Validators.required);
      }

      officialDocumentIdControl?.updateValueAndValidity();
    });
  }

  fillForm(): void {
    this.form = this.formBuilder.group(
      {
        name: ['', [Validators.required]],
        surname: ['', [Validators.required]],
        officialDocumentId: [''],
        address: ['', [Validators.minLength(MIN_LENGTH_ADDRESS), Validators.maxLength(MAX_LENGTH_ADDRESS)]],
        phone: ['', [phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]],
        authEmail: ['', [emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]],
        email: ['', [emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]],
        countryId: [-1, [Validators.required, Validators.min(0)]],
        isIdVerifiedInPerson: [false, [Validators.required]],
        birthday: ['', [Validators.required]],
        plannedAuthType: ['unknown', Validators.required],
        nationalDigitalId: ['', [Validators.pattern(/^\d*-?\d*$/), Validators.minLength(11), Validators.maxLength(12)]],
        verifierFullName: [''],
        verifiedById: [null],
        agreementToPortalTerms: [false]
      },
      { validators: [atLeastOneContactInfoRequired(), onlyOneAuthFieldAllowed()] }
    );

    this.form.get('isIdVerifiedInPerson')?.valueChanges.subscribe((val) => {
      if (val) {
        const userSecurityInfo = localStorage.getItem(USER_SECURITY_INFO_KEY);
        this.form.get('verifiedById')?.setValue(userSecurityInfo ? JSON.parse(userSecurityInfo).userId : null);
        this.form.get('verifierFullName')?.setValue(this.authService.getUserFullName());
      } else {
        this.form.get('verifiedById')?.reset();
        this.form.get('verifierFullName')?.reset();
      }
    });

    this.applyPlannedAuthTypeRules(this.plannedAuthType?.value);

    this.plannedAuthType?.valueChanges.subscribe((type) => {
      this.authEmail?.setValue('', { emitEvent: false });
      this.nationalDigitalId?.setValue('', { emitEvent: false });

      this.applyPlannedAuthTypeRules(type);
    });
  }

  private applyPlannedAuthTypeRules(type: 'email' | 'latviaLv' | 'unknown' | null | undefined): void {
    const authEmailCtrl = this.authEmail;
    const natIdCtrl = this.nationalDigitalId;

    if (!authEmailCtrl || !natIdCtrl) return;

    authEmailCtrl.removeValidators(Validators.required);
    natIdCtrl.removeValidators(Validators.required);

    if (type === 'email') {
      authEmailCtrl.addValidators(Validators.required);
    } else if (type === 'latviaLv') {
      natIdCtrl.addValidators(Validators.required);
    }

    authEmailCtrl.updateValueAndValidity({ emitEvent: false });
    natIdCtrl.updateValueAndValidity({ emitEvent: false });

    authEmailCtrl.markAsPristine();
    authEmailCtrl.markAsUntouched();
    natIdCtrl.markAsPristine();
    natIdCtrl.markAsUntouched();
  }

  submit(): void {
    this.submitAttempted = true;

    if (this.form.valid) {
      this.isLoading = true;
      const { plannedAuthType, ...formValue } = this.form.value;
      const args = {
        ...formValue,
        nationalDigitalId: this.nationalDigitalId?.value,
        officialDocumentId: this.officialDocumentId?.value.replace(/-/g, ''),
        status: CLIENT_STATUS_UNFINISHED,
        clientType: CLIENT_TYPE_INDIVIDUAL,
        authenticationType: PLANNED_AUTH_TYPE_TO_API[plannedAuthType] ?? 'Local'
      };

      this.apiService.createClient(args).subscribe(
        (data: ClientCard) => {
          this.toastService.showSuccess('addClientSuccess', '', {
            shouldTranslate: true
          });
          this.router.navigate([`/klients/${data.id}`]);
          this.isLoading = false;
        },
        (error: any) => {
          this.isLoading = false;
          const errors = error?.error?.errors as Record<string, string[] | undefined> | undefined;
          if (errors?.['nationalDigitalId']?.length) {
            this.toastService.showDanger('clientIdentifierExistsNationalId', '', { shouldTranslate: true });
          } else if (errors?.['officialDocumentId']?.length) {
            this.toastService.showDanger('clientIdentifierExistsOfficialId', '', { shouldTranslate: true });
          } else if (errors?.['email']?.length) {
            this.toastService.showDanger('emailAlreadyInUse', '', { shouldTranslate: true });
          } else if (errors?.['authEmail']?.length) {
            this.toastService.showDanger('emailAlreadyInUse', '', { shouldTranslate: true });
          } else {
            this.toastService.showRequestError();
          }
        }
      );
    } else {
      const atLeastOneError = this.form.errors?.['atLeastOneContactInfoRequired'];
      if (atLeastOneError) {
        this.toastService.showDanger('contactInfoError', '', {
          shouldTranslate: true
        });
      }

      const onlyOneAuthFieldAllowed = this.form.errors?.['onlyOneAuthFieldAllowed'];
      if (onlyOneAuthFieldAllowed) {
        this.toastService.showDanger('onlyOneAuthFieldAllowedError', '', {
          shouldTranslate: true
        });
      }

      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'pages.client');
    }
  }

  back(): void {
    this.location.back();
  }

  shouldRenderError(key: string): boolean {
    if (this.submitAttempted) {
      return true;
    }

    const control = this.form.get(key);
    if (control) {
      return control.invalid && control.touched && !!control?.errors;
    }

    return false;
  }

  onCountryBlur(): void {
    // TODO
  }

  onDateSelected(date: string, key: string): void {
    this.form.get(key)?.setValue(date);
    this.form.get(key)?.markAsTouched();
  }

  allowOnlyNumbersAndSingleDash(event: KeyboardEvent) {
    const allowed = /[0-9-]/;
    if (!allowed.test(event.key)) {
      event.preventDefault();
    }

    // prevent second dash
    if (event.key === '-' && (event.target as HTMLInputElement).value.includes('-')) {
      event.preventDefault();
    }
  }

  validateSingleDash(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/(.*-.*)-+/, '$1');
  }
}
